import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Duration in milliseconds before controls auto-hide
 */
const AUTO_HIDE_DELAY_MS = 1000;

export type UseAutoHideControlsProps = Readonly<{
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onControlsVisibilityChange?: (visible: boolean) => void;
}>;

export type UseAutoHideControlsReturn = {
  isVisible: boolean;
  handleMouseMove: () => void;
  handleControlsMouseEnter: () => void;
  handleControlsMouseLeave: () => void;
  showControls: () => void;
  cursorStyle: "default" | "none";
};

/**
 * Custom hook to manage auto-hiding controls
 * Controls auto-hide after inactivity delay, regardless of play/pause state
 */
export function useAutoHideControls(props: UseAutoHideControlsProps): UseAutoHideControlsReturn {
  const { videoRef, onControlsVisibilityChange } = props;

  const [showChrome, setShowChrome] = useState(true);
  const [isHoveringControls, setIsHoveringControls] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current) globalThis.clearTimeout(hideTimerRef.current);
    // Schedule hide if not hovering over controls (regardless of play/pause state)
    if (videoRef.current && !isHoveringControls) {
      hideTimerRef.current = globalThis.setTimeout(() => setShowChrome(false), AUTO_HIDE_DELAY_MS);
    }
  }, [videoRef, isHoveringControls]);

  const handleMouseMove = useCallback(() => {
    setShowChrome(true);
    scheduleHide();
  }, [scheduleHide]);

  const handleControlsMouseEnter = useCallback(() => {
    setIsHoveringControls(true);
    // Cancel any pending hide when entering controls
    if (hideTimerRef.current) {
      globalThis.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const handleControlsMouseLeave = useCallback(() => {
    setIsHoveringControls(false);
    // Resume auto-hide when leaving controls
    scheduleHide();
  }, [scheduleHide]);

  const showControls = useCallback(() => {
    setShowChrome(true);
    scheduleHide();
  }, [scheduleHide]);

  // Notify parent when controls visibility changes
  useEffect(() => {
    onControlsVisibilityChange?.(showChrome);
  }, [showChrome, onControlsVisibilityChange]);

  // Initialize auto-hide timer on mount and when window gains focus
  useEffect(() => {
    // Start auto-hide timer on mount (even if mouse hasn't moved)
    scheduleHide();

    // Handle window focus/blur to ensure controls hide after regaining focus
    const handleFocus = () => {
      setShowChrome(true);
      scheduleHide();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
      if (hideTimerRef.current) {
        globalThis.clearTimeout(hideTimerRef.current);
      }
    };
  }, [scheduleHide]);

  // Controls visibility is determined solely by showChrome state (auto-hide works for both playing and paused)
  const isVisible = showChrome;

  return {
    isVisible,
    handleMouseMove,
    handleControlsMouseEnter,
    handleControlsMouseLeave,
    showControls,
    cursorStyle: showChrome ? "default" : "none",
  };
}
